// Firestore collections & security: see firestore.rules in repo root; deploy via Firebase Console or CLI.
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Validate Firebase config
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error(
    "Firebase configuration is missing. Please check your .env file and ensure all REACT_APP_FIREBASE_* variables are set."
  );
}

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const provider = new GoogleAuthProvider();

// Save test result to user_test_results collection
export const saveTest = async (userId, testCode, scorePercent, email, name) => {
  try {
    const displayName = name || "";
    await addDoc(collection(db, "user_test_results"), {
      user_id: userId,
      test_code: testCode,
      score_percent: scorePercent,
      email: email || "",
      name: displayName,
      name_lower: displayName.trim().toLowerCase(),
      completed_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error saving test result:", error);
    throw error;
  }
};

// Get all test results for a user (single query)
export const getAllUserTestResults = async userId => {
  try {
    const q = query(
      collection(db, "user_test_results"),
      where("user_id", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    // Return as Map for easy lookup: { test_code: { score_percent, completed_at } }
    const results = new Map();
    querySnapshot.forEach(doc => {
      const data = doc.data();
      results.set(data.test_code, {
        score_percent: data.score_percent,
        completed_at: data.completed_at,
        id: doc.id,
      });
    });

    return results;
  } catch (error) {
    console.error("Error getting user test results:", error);
    return new Map();
  }
};

// Check if user passed a specific test (using cached results or query)
export const isTestPassed = async (userId, testCode, cachedResults = null) => {
  try {
    // If cached results provided, use them (no query needed)
    if (cachedResults instanceof Map) {
      return cachedResults.has(testCode);
    }

    // Otherwise, query for this specific test
    const q = query(
      collection(db, "user_test_results"),
      where("user_id", "==", userId),
      where("test_code", "==", testCode)
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking test result:", error);
    return false;
  }
};

const mapTestResultDoc = docSnap => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    user_id: data.user_id,
    test_code: data.test_code,
    score_percent: data.score_percent,
    email: data.email || "",
    name: data.name || "",
    completed_at: data.completed_at,
  };
};

/** Firestore `in` supports up to 30 values per query. */
const USER_ID_IN_CHUNK = 30;

/**
 * Load all user_test_results rows for the given user ids (multiple attempts per user).
 */
const fetchAllRowsForUserIds = async userIds => {
  if (userIds.length === 0) {
    return [];
  }
  const coll = collection(db, "user_test_results");
  const rows = [];
  for (let i = 0; i < userIds.length; i += USER_ID_IN_CHUNK) {
    const chunk = userIds.slice(i, i + USER_ID_IN_CHUNK);
    const q = query(coll, where("user_id", "in", chunk));
    const snap = await getDocs(q);
    snap.forEach(d => rows.push(mapTestResultDoc(d)));
  }
  return rows;
};

const mapUserProfileDoc = d => {
  const data = d.data();
  return {
    user_id: d.id,
    name: data.name || "",
    email: data.email || "",
  };
};

/**
 * Admin report: one page = `participantPageSize` rows from the `users` collection (everyone who logged in),
 * then all `user_test_results` for those uids (may be empty). Users with no tests still appear.
 * - No search: orderBy createdAt desc on `users`.
 * - Search: name_lower / legacy `name` prefix on `users`.
 * `cursorDoc` is the last Firestore user doc from the previous page.
 */
export const getParticipantTestResultsPage = async (
  participantPageSize,
  cursorDoc,
  options = {}
) => {
  const opts =
    typeof options === "string"
      ? {
          nameSearch: options,
          nameSearchRaw: "",
          resolvedSearchField: null,
          legacyNamePrefix: null,
          excludedUserIdsFromPriorPages: [],
        }
      : {
          nameSearch: options.nameSearch ?? "",
          nameSearchRaw: options.nameSearchRaw ?? "",
          resolvedSearchField: options.resolvedSearchField ?? null,
          legacyNamePrefix: options.legacyNamePrefix ?? null,
          excludedUserIdsFromPriorPages:
            options.excludedUserIdsFromPriorPages ?? [],
        };

  const prefix = (opts.nameSearch || "").trim().toLowerCase();
  const rawTrim = (opts.nameSearchRaw || "").trim();
  const { resolvedSearchField } = opts;
  const legacyPrefixLocked =
    opts.legacyNamePrefix != null && opts.legacyNamePrefix !== ""
      ? opts.legacyNamePrefix
      : null;
  const excludedUserIdsFromPriorPages = opts.excludedUserIdsFromPriorPages;

  const tryLegacyNamePrefixes = () => {
    const list = [];
    if (rawTrim) list.push(rawTrim);
    const titled =
      prefix.length > 0 ? prefix.charAt(0).toUpperCase() + prefix.slice(1) : "";
    if (titled && !list.includes(titled)) list.push(titled);
    return list;
  };

  try {
    const usersColl = collection(db, "users");

    const finishPage = async (profiles, lastDoc, hasMoreFlag) => {
      const ids = profiles.map(p => p.user_id);
      const rows = await fetchAllRowsForUserIds(ids);
      return {
        rows,
        pageProfiles: profiles,
        lastVisible: lastDoc,
        hasMore: hasMoreFlag,
        includedUserIds: ids,
        resolvedSearchField: null,
        legacyNamePrefix: null,
      };
    };

    const runUserDiscovery = async buildBatchQuery => {
      const excluded = new Set(excludedUserIdsFromPriorPages || []);
      const profiles = [];
      let walkCursor = cursorDoc || null;
      const batchLimit = 100;
      let lastDiscoveryDoc = null;

      while (profiles.length < participantPageSize) {
        const q = buildBatchQuery(walkCursor, batchLimit);
        const snap = await getDocs(q);
        if (snap.empty) {
          break;
        }
        for (const d of snap.docs) {
          const uid = d.id;
          if (excluded.has(uid)) {
            continue;
          }
          profiles.push(mapUserProfileDoc(d));
          lastDiscoveryDoc = d;
          if (profiles.length === participantPageSize) {
            const peekQ = buildBatchQuery(d, 1);
            const peekSnap = await getDocs(peekQ);
            return finishPage(profiles, d, !peekSnap.empty);
          }
        }
        walkCursor = snap.docs[snap.docs.length - 1];
        if (snap.docs.length < batchLimit) {
          break;
        }
      }

      return finishPage(profiles, lastDiscoveryDoc, false);
    };

    if (prefix.length === 0) {
      const res = await runUserDiscovery((cDoc, lim) =>
        cDoc
          ? query(
              usersColl,
              orderBy("createdAt", "desc"),
              startAfter(cDoc),
              limit(lim)
            )
          : query(usersColl, orderBy("createdAt", "desc"), limit(lim))
      );
      return res;
    }

    if (resolvedSearchField === "name_lower") {
      const p = prefix.trim().toLowerCase();
      const res = await runUserDiscovery((cDoc, lim) =>
        cDoc
          ? query(
              usersColl,
              where("name_lower", ">=", p),
              where("name_lower", "<=", p + "\uf8ff"),
              orderBy("name_lower"),
              startAfter(cDoc),
              limit(lim)
            )
          : query(
              usersColl,
              where("name_lower", ">=", p),
              where("name_lower", "<=", p + "\uf8ff"),
              orderBy("name_lower"),
              limit(lim)
            )
      );
      return {
        ...res,
        resolvedSearchField: "name_lower",
        legacyNamePrefix: null,
      };
    }

    if (resolvedSearchField === "name") {
      const nameBound =
        legacyPrefixLocked || rawTrim || tryLegacyNamePrefixes()[0];
      if (!nameBound) {
        return {
          rows: [],
          pageProfiles: [],
          lastVisible: null,
          hasMore: false,
          includedUserIds: [],
          resolvedSearchField: "name",
          legacyNamePrefix: null,
        };
      }
      const np = nameBound.trim();
      const res = await runUserDiscovery((cDoc, lim) =>
        cDoc
          ? query(
              usersColl,
              where("name", ">=", np),
              where("name", "<=", np + "\uf8ff"),
              orderBy("name"),
              startAfter(cDoc),
              limit(lim)
            )
          : query(
              usersColl,
              where("name", ">=", np),
              where("name", "<=", np + "\uf8ff"),
              orderBy("name"),
              limit(lim)
            )
      );
      return {
        ...res,
        resolvedSearchField: "name",
        legacyNamePrefix: np,
      };
    }

    if (!cursorDoc) {
      const qLowerFirst = query(
        usersColl,
        where("name_lower", ">=", prefix),
        where("name_lower", "<=", prefix + "\uf8ff"),
        orderBy("name_lower"),
        limit(1)
      );
      const snapLower = await getDocs(qLowerFirst);
      if (!snapLower.empty) {
        return await getParticipantTestResultsPage(participantPageSize, null, {
          ...opts,
          resolvedSearchField: "name_lower",
        });
      }
      for (const np of tryLegacyNamePrefixes()) {
        const qn = query(
          usersColl,
          where("name", ">=", np),
          where("name", "<=", np + "\uf8ff"),
          orderBy("name"),
          limit(1)
        );
        const sn = await getDocs(qn);
        if (!sn.empty) {
          return await getParticipantTestResultsPage(
            participantPageSize,
            null,
            {
              ...opts,
              resolvedSearchField: "name",
              legacyNamePrefix: np,
            }
          );
        }
      }
      return {
        rows: [],
        pageProfiles: [],
        lastVisible: null,
        hasMore: false,
        includedUserIds: [],
        resolvedSearchField: null,
        legacyNamePrefix: null,
      };
    }

    throw new Error(
      "Participant search: when using Next/Previous, pass resolvedSearchField from the first page response (and legacyNamePrefix for name search)."
    );
  } catch (error) {
    console.error("Error getting participant test results page:", error);
    throw error;
  }
};

// Get count of passed tests for a user
export const getPassedTestsCount = async userId => {
  try {
    const results = await getAllUserTestResults(userId);
    return results.size;
  } catch (error) {
    console.error("Error getting passed tests count:", error);
    return 0;
  }
};
